import useGetDeliveryLocation from '../../modules/mobile/common/hooks/useGetDeliveryLocation';
import { franchisesApiSlice, useLazyGetDeliveryCompatibleCompaniesQuery } from '../../store/api-slices';
import { useSelector } from 'react-redux';
import { selectFranchiseId } from '../../store/slices/InitialDataSlice';
import { DeliveryAddressSchema } from '../../modules/mobile/modals/delivery-address/form/schema';
import { useLocationData } from './provider/LocationDataProvider';
import { GooglePlaceResource } from '../../services/resources/GooglePlaceResource';
import { useFormContext } from 'react-hook-form';
import { ERROR_CODES, ORDER_METHODS } from '../../services/exports/Constants';

const BACKEND_FIELD_MAP: Readonly<Record<string, keyof DeliveryAddressSchema>> = {
  zip_code: 'delivery_zip_code',
  street_name: 'delivery_street_name',
  street_number: 'delivery_street_number',
  city: 'delivery_city',
  // The address country only decides which postcode format applies.
  country: 'delivery_zip_code',
};

const useDeliveryCompatibleStuff = () => {
  const deliveryAddressForm = useFormContext<DeliveryAddressSchema>();
  const { company, orderMethod, setNoCouriersError, resetError: resetValidationError } = useLocationData();
  const [getDeliveryCompatibleCompanies] = useLazyGetDeliveryCompatibleCompaniesQuery();
  const franchiseId = useSelector(selectFranchiseId);
  const { getLocation, loading, validationError, resetError } = useGetDeliveryLocation(company);
  const deliveryFormValues = deliveryAddressForm.watch();
  const queryState = useSelector(
    franchisesApiSlice.endpoints.getDeliveryCompatibleCompanies.select({
      franchiseId,
      latitude: deliveryFormValues.delivery_lat,
      longitude: deliveryFormValues.delivery_lng,
      street_name: deliveryFormValues.delivery_street_name,
      street_number: deliveryFormValues.delivery_street_number,
      city: deliveryFormValues.delivery_city,
      zip_code: deliveryFormValues.delivery_zip_code,
      country: deliveryFormValues.delivery_country,
    }),
  );
  const submitHandler = async (data: DeliveryAddressSchema, selectedPlace?: GooglePlaceResource) => {
    const { location, success, country } = await getLocation(data, selectedPlace);

    if (!success || !location) {
      return;
    }

    deliveryAddressForm.setValue('delivery_lat', location.lat());
    deliveryAddressForm.setValue('delivery_lng', location.lng());
    // Read back from the lookup, so a hand-typed address carries the country it resolved to.
    deliveryAddressForm.setValue('delivery_country', country ?? null);

    if (orderMethod !== ORDER_METHODS.DELIVERY) {
      return;
    }

    resetValidationError();

    await getDeliveryCompatibleCompanies(
      {
        franchiseId,
        latitude: location.lat(),
        longitude: location.lng(),
        street_name: data.delivery_street_name,
        street_number: data.delivery_street_number,
        city: data.delivery_city,
        zip_code: data.delivery_zip_code,
        country: data.delivery_country,
      },
      true,
    )
      .unwrap()
      .catch((error) => {
        const body = error?.data?.response?.data;

        if (body?.error_code === ERROR_CODES.NO_COURIERS_AVAILABLE) {
          setNoCouriersError(body?.payload?.master_promo_code);

          return;
        }

        if (!body?.errors) {
          return;
        }

        Object.entries(body.errors).forEach(([field, messages]) => {
          const formField = BACKEND_FIELD_MAP[field];

          if (!formField) {
            return;
          }

          deliveryAddressForm.setError(formField, {
            type: 'server',
            message: (messages as string[])[0],
          });
        });
      });
  };

  const handle = (selectedPlace?: GooglePlaceResource) =>
    deliveryAddressForm?.handleSubmit((data) => submitHandler(data, selectedPlace))();

  return { handle, queryState, isLoading: loading || queryState.isLoading, validationError, resetError };
};

export default useDeliveryCompatibleStuff;
